import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, ChevronUp, ChevronDown, Trash2, X, ListChecks } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import SidePanel from '../SidePanel';
import ModalShell from '../ui/ModalShell';
import Dropdown from '../receptura/Dropdown';
import { SECTION_COLOR, SECTION_BG, TYP_POLOZKY, hlavicky } from './ui';

/**
 * Checklisty → Šablony. Norma §1: seznam šablon · detail = položky šablony
 * (pořadí šipkami, klik = úprava v inspektoru §1f) · třetí sloupec „Kde se
 * používá" (šablony směn a fáze) a plnění za 30 dní.
 * Uložení posílá celé položky s `id` — server existující upraví, nové přidá
 * a chybějící vyřadí (historie odpovědí zůstává).
 */
const POHLEDY = [
    { id: 'aktivni', label: 'Aktivní', patri: s => s.stav !== 'archivovano' },
    { id: 'nepouzite', label: 'Nepoužité', upozorneni: true, patri: s => s.stav !== 'archivovano' && !(s.pouziti || []).length },
    { id: 'archiv', label: 'Archiv', patri: s => s.stav === 'archivovano' },
];
const DNY = [[1, 'Po'], [2, 'Út'], [3, 'St'], [4, 'Čt'], [5, 'Pá'], [6, 'So'], [7, 'Ne']];
const TYPY = Object.entries(TYP_POLOZKY).map(([value, label]) => ({ value, label }));

const parseMoznosti = (json) => {
    let m = [];
    try { m = JSON.parse(json || '[]'); } catch { m = []; }
    return (Array.isArray(m) ? m : []).map(x => (typeof x === 'string' ? { text: x } : x));
};
const opakovaniText = (p) => {
    if (p.opakovani_typ === 'dny_v_tydnu' && p.opakovani_dny_tydne) {
        return 'jen ' + p.opakovani_dny_tydne.split(',').map(d => DNY.find(x => x[0] === Number(d))?.[1]).filter(Boolean).join(', ');
    }
    return p.opakovani_typ ? `opakování: ${p.opakovani_typ}` : null;
};

function PolozkaEditor({ polozka, parametry, isDark, isMobile, iStyle, mutedColor, topOffset, onUlozit, onSmazat, onClose }) {
    const [p, setP] = useState(polozka);
    const zmen = (k, v) => setP(x => ({ ...x, [k]: v }));
    const moznosti = parseMoznosti(p.moznosti_json);
    const dny = (p.opakovani_dny_tydne || '').split(',').filter(Boolean).map(Number);
    const jenDny = p.opakovani_typ === 'dny_v_tydnu';
    const label = (t) => <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block', color: mutedColor }}>{t}</label>;
    const platne = p.text?.trim() && (p.typ_odpovedi !== 'cislo' || p.parametr_id);

    return (
        <SidePanel open onClose={onClose} title={polozka.id ? 'Upravit položku' : 'Nová položka'} isDark={isDark} isMobile={isMobile}
            width={420} modal={false} accent={SECTION_COLOR} topOffset={topOffset}
            footer={(
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', padding: '12px 16px' }}>
                    {onSmazat ? <button type="button" onClick={onSmazat} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', color: '#EF4444', background: 'rgba(239,68,68,0.08)', fontSize: 13 }}><Trash2 size={14} /> Odebrat</button> : <span />}
                    <button type="button" disabled={!platne} onClick={() => onUlozit(p)} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', background: SECTION_COLOR, color: '#fff', fontWeight: 700, fontSize: 13, opacity: platne ? 1 : 0.5 }}>Uložit</button>
                </div>
            )}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>{label('Typ')}
                    <Dropdown isDark={isDark} value={p.typ_odpovedi} options={TYPY}
                        onChange={v => setP(x => ({ ...x, typ_odpovedi: v, text: v === 'cislo' && !x.text ? 'Měření' : x.text }))} /></div>
                {p.typ_odpovedi === 'cislo' && (
                    <div>{label('Měřicí bod')}
                        <Dropdown isDark={isDark} value={p.parametr_id ?? ''} placeholder="Vyber měřicí bod…"
                            options={parametry.filter(x => x.aktivni).map(x => ({ value: x.id, label: `${x.nazev}${x.jednotka ? ` (${x.jednotka})` : ''}` }))}
                            onChange={v => { const par = parametry.find(x => String(x.id) === String(v)); setP(x => ({ ...x, parametr_id: v || null, text: par ? `Měření: ${par.nazev}` : x.text })); }} />
                        <p style={{ fontSize: 11.5, color: mutedColor, margin: '6px 0 0' }}>Limit, jednotku a kontrolu při psaní si položka vezme z bodu.</p></div>
                )}
                <div>{label(p.typ_odpovedi === 'cislo' ? 'Text v checklistu' : 'Co udělat / zkontrolovat')}
                    <input value={p.text || ''} onChange={e => zmen('text', e.target.value)} style={iStyle} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={p.povinne !== false} onChange={e => zmen('povinne', e.target.checked)} className="accent-cyan-500" style={{ width: 16, height: 16 }} />
                    Povinné — bez ní fázi směny nedokončí
                </label>
                {['kontrola', 'vyber', 'checkbox', 'foto', 'text'].includes(p.typ_odpovedi) && (
                    <div>{label(p.typ_odpovedi === 'kontrola' ? 'Co dělat při NOK' : 'Instrukce')}
                        <input value={p.instrukce_mereni || ''} onChange={e => zmen('instrukce_mereni', e.target.value)} style={iStyle} placeholder="volitelné" /></div>
                )}
                {p.typ_odpovedi === 'vyber' && (
                    <div>{label('Možnosti (klepnutím na ! označíš negativní = odchylka)')}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                            {moznosti.map((m, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '3px 8px', borderRadius: 6, color: m.negativni ? '#EF4444' : 'var(--text-primary)', border: `1px solid ${m.negativni ? 'rgba(239,68,68,0.4)' : isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                                    {m.text}
                                    <button type="button" onClick={() => zmen('moznosti_json', JSON.stringify(moznosti.map((x, j) => j === i ? { ...x, negativni: !x.negativni } : x)))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.negativni ? '#EF4444' : mutedColor, padding: 0 }}>!</button>
                                    <button type="button" onClick={() => zmen('moznosti_json', JSON.stringify(moznosti.filter((_, j) => j !== i)))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0, display: 'flex' }}><X size={11} /></button>
                                </span>
                            ))}
                        </div>
                        <input style={iStyle} placeholder="Napiš možnost a Enter…" onKeyDown={e => { if (e.key === 'Enter' && e.currentTarget.value.trim()) { e.preventDefault(); zmen('moznosti_json', JSON.stringify([...moznosti, { text: e.currentTarget.value.trim() }])); e.currentTarget.value = ''; } }} />
                    </div>
                )}
                <div>{label('Kdy se v checklistu objeví')}
                    <Dropdown isDark={isDark} value={jenDny ? 'dny' : ''} options={[{ value: '', label: 'Pokaždé' }, { value: 'dny', label: 'Jen ve vybrané dny' }]}
                        onChange={v => setP(x => ({ ...x, opakovani_typ: v === 'dny' ? 'dny_v_tydnu' : null, opakovani_dny_tydne: v === 'dny' ? (x.opakovani_dny_tydne || '1') : null }))} />
                    {jenDny && (
                        <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
                            {DNY.map(([d, n]) => {
                                const on = dny.includes(d);
                                return (
                                    <button key={d} type="button" onClick={() => { const nove = on ? dny.filter(x => x !== d) : [...dny, d].sort(); zmen('opakovani_dny_tydne', nove.join(',') || null); }}
                                        style={{ width: 40, height: 34, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 12.5, border: `1px solid ${on ? SECTION_COLOR : isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}`, background: on ? SECTION_BG : 'transparent', color: on ? SECTION_COLOR : 'var(--text-secondary)' }}>{n}</button>
                                );
                            })}
                        </div>
                    )}
                    <p style={{ fontSize: 11.5, color: mutedColor, margin: '6px 0 0' }}>Třeba lednice jen v pondělí — ostatní dny se položka v checklistu vůbec neukáže.</p>
                </div>
            </div>
        </SidePanel>
    );
}

export default function SablonyZalozka({ isDark, isMobile, cardStyle, inputStyle, mutedColor, upravuje, rezervaDole = null }) {
    const [sablony, setSablony] = useState(null);
    const [parametry, setParametry] = useState([]);
    const [pohled, setPohled] = useState('aktivni');
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [editPolozka, setEditPolozka] = useState(null);   // { polozka, index }
    const [editHlavicka, setEditHlavicka] = useState(null);
    const [nova, setNova] = useState(null);
    const [chyba, setChyba] = useState(null);

    const nacti = useCallback(() => {
        fetch('/api/checklisty/sablony', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : [])).then(setSablony).catch(() => setSablony([]));
    }, []);
    useEffect(() => { nacti(); fetch('/api/checklisty/parametry?vse=1').then(r => r.json()).then(setParametry).catch(() => {}); }, [nacti]);
    const nactiDetail = useCallback(() => {
        if (!vybranyId) return;
        fetch(`/api/checklisty/sablony/${vybranyId}`, { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : null)).then(setDetail).catch(() => {});
    }, [vybranyId]);
    useEffect(() => { setDetail(null); nactiDetail(); }, [nactiDetail]);

    const obsahRef = useRef(null);
    const [top, setTop] = useState(0);
    useEffect(() => {
        const zmer = () => { const el = obsahRef.current; if (el) setTop(Math.max(0, Math.round(el.getBoundingClientRect().top))); };
        zmer(); window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [vybranyId, editPolozka, editHlavicka]);

    // Uložení šablony s celými položkami (server: úprava podle id, nové přidá, chybějící vyřadí)
    const uloz = async (zmeny) => {
        setChyba(null);
        const polozky = (zmeny.polozky ?? detail.polozky).map(p => ({
            id: p.id ?? null, text: p.text, typ_odpovedi: p.typ_odpovedi, povinne: p.povinne !== false,
            parametr_id: p.parametr_id ? Number(p.parametr_id) : null, instrukce_mereni: p.instrukce_mereni || null,
            moznosti_json: p.moznosti_json || null, opakovani_typ: p.opakovani_typ || null, opakovani_dny_tydne: p.opakovani_dny_tydne || null,
        }));
        const res = await fetch(`/api/checklisty/sablony/${detail.id}`, {
            method: 'PUT', headers: hlavicky(),
            body: JSON.stringify({ nazev: zmeny.nazev ?? detail.nazev, popis: zmeny.popis ?? detail.popis, stav: zmeny.stav ?? detail.stav, polozky }),
        });
        if (!res.ok) { const d = await res.json().catch(() => ({})); setChyba(d.error || d.message || 'Uložení se nepovedlo'); return false; }
        setDetail(await res.json());
        nacti();
        return true;
    };
    const posun = (i, smer) => {
        const p = [...detail.polozky];
        const j = i + smer;
        if (j < 0 || j >= p.length) return;
        [p[i], p[j]] = [p[j], p[i]];
        uloz({ polozky: p });
    };
    const zalozit = async () => {
        if (!nova?.trim()) return;
        const res = await fetch('/api/checklisty/sablony', { method: 'POST', headers: hlavicky(), body: JSON.stringify({ nazev: nova.trim(), polozky: [{ text: 'Nová položka', typ_odpovedi: 'checkbox', povinne: true }] }) });
        if (res.ok) { const s = await res.json(); setNova(null); nacti(); setVybranyId(s.id); }
    };

    const vse = sablony || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, vse.filter(p.patri).length])), [vse]);
    const akt = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const q = hledani.trim().toLowerCase();
    const seznam = vse.filter(s => akt.patri(s) && (!q || `${s.nazev} ${(s.pouziti || []).map(x => x.kod).join(' ')}`.toLowerCase().includes(q)));
    const vybrana = vse.find(s => s.id === vybranyId);
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const hrana = isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)';
    const muted = mutedColor;
    const tlac = { display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${hrana}` };

    const radek = (s, siroky) => {
        const sel = s.id === vybranyId;
        const kde = [...new Set((s.pouziti || []).map(x => x.kod))].join(', ');
        return (
            <button key={s.id} type="button" onClick={() => setVybranyId(s.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '10px 14px', minHeight: 56, background: sel ? SECTION_BG : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{s.nazev}</p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: muted }}>
                        {s.polozky_count} {s.polozky_count === 1 ? 'položka' : s.polozky_count < 5 ? 'položky' : 'položek'}{!siroky && (kde ? ` · ${kde}` : ' · nikde se nepoužívá')}
                    </p>
                </div>
                {siroky && <span className="truncate" style={{ width: 220, fontSize: 12, color: kde ? muted : '#F59E0B' }}>{kde || 'nikde se nepoužívá'}</span>}
                {siroky && <span style={{ width: 110, fontSize: 12, color: muted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.stat?.vyplneno ?? 0}× / 30 dní</span>}
                {siroky && <span style={{ width: 80, fontSize: 12, textAlign: 'right', color: s.stat?.odchylky ? '#EF4444' : muted }}>{s.stat?.odchylky ? `${s.stat.odchylky} odch.` : '—'}</span>}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat šablonu nebo směnu…', placeholderKratky: 'Hledat' }}
                akce={upravuje ? [{ id: 'nova', label: 'Nová šablona', Icon: Plus, primarni: true, onClick: () => setNova('') }] : []}
                pohled={POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={akt.id === p.id} upozorneni={p.upozorneni && pocty[p.id] > 0}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))} />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {sablony === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {sablony && seznam.length === 0 && <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>{q ? 'Nic se neshoduje.' : 'Nic tu není.'}</p>}
                {seznam.map(s => radek(s, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detailPanel = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <div style={{ padding: isMobile ? '6px 14px 12px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                {isMobile && <ZpetTlacitko onClick={() => setVybranyId(null)} popis="Zpět na šablony" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 17, margin: 0, color: 'var(--text-primary)' }}>{detail?.nazev || vybrana?.nazev || '…'}</h2>
                        {detail?.popis && <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>{detail.popis}</p>}
                        {detail?.stav === 'archivovano' && <p className="text-xs" style={{ color: '#F59E0B', margin: '4px 0 0' }}>Archivovaná — nejde upravovat.</p>}
                    </div>
                    {upravuje && detail && detail.stav !== 'archivovano' && (
                        <button type="button" onClick={() => setEditHlavicka({ nazev: detail.nazev, popis: detail.popis || '' })} style={tlac}><Pencil size={14} /> Upravit</button>
                    )}
                </div>
            </div>
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '10px 14px' : '12px 18px' }}>
                {chyba && <p className="text-sm" style={{ color: '#EF4444', margin: '0 0 8px' }}>{chyba}</p>}
                {!detail ? <p className="text-sm" style={{ color: muted }}>Načítám…</p> : (<>
                    {detail.polozky.map((p, i) => {
                        const par = parametry.find(x => x.id === p.parametr_id);
                        const meta = [TYP_POLOZKY[p.typ_odpovedi], par && `bod ${par.nazev}`, p.povinne ? 'povinné' : 'nepovinné', opakovaniText(p)].filter(Boolean).join(' · ');
                        const lze = upravuje && detail.stav !== 'archivovano';
                        return (
                            <div key={p.id ?? i} className="flex items-center" style={{ gap: 8, padding: '8px 10px', borderRadius: 10, marginBottom: 6, border: `1px solid ${cara}`, background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)' }}>
                                <button type="button" disabled={!lze} onClick={() => setEditPolozka({ polozka: p, index: i })} className="flex-1 min-w-0 text-left" style={{ background: 'none', border: 'none', cursor: lze ? 'pointer' : 'default', padding: 0 }}>
                                    <p className="truncate font-semibold" style={{ margin: 0, fontSize: 13.5, color: 'var(--text-primary)' }}>{p.text}</p>
                                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: muted }}>{meta}</p>
                                </button>
                                {lze && (
                                    <div className="flex" style={{ gap: 2 }}>
                                        <button type="button" onClick={() => posun(i, -1)} disabled={i === 0} aria-label="Posunout výš" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, opacity: i === 0 ? 0.3 : 1, padding: 4 }}><ChevronUp size={16} /></button>
                                        <button type="button" onClick={() => posun(i, 1)} disabled={i === detail.polozky.length - 1} aria-label="Posunout níž" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, opacity: i === detail.polozky.length - 1 ? 0.3 : 1, padding: 4 }}><ChevronDown size={16} /></button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {upravuje && detail.stav !== 'archivovano' && (
                        <button type="button" onClick={() => setEditPolozka({ polozka: { text: '', typ_odpovedi: 'checkbox', povinne: true }, index: null })} style={{ ...tlac, marginTop: 4, color: SECTION_COLOR, borderColor: `${SECTION_COLOR}66` }}>
                            <Plus size={14} /> Přidat položku
                        </button>
                    )}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </>)}
            </div>
        </div>
    );

    const panel = () => {
        const s = vybrana;
        const r = (l, h, cervene) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}`, gap: 10 }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: cervene && h > 0 ? '#F87171' : 'var(--text-primary)', textAlign: 'right' }}>{h}</b>
            </div>
        );
        const nadpis = (t) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '12px 0 6px' }}>{t}</p>;
        if (!s) {
            return (
                <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                    {nadpis('Šablony')}
                    {r('Aktivní', pocty.aktivni)}
                    {r('Nepoužité', pocty.nepouzite, true)}
                    {r('Vyplněno za 30 dní', vse.reduce((a, x) => a + (x.stat?.vyplneno || 0), 0))}
                    <p className="text-xs" style={{ color: muted, marginTop: 12 }}>Šablona se do směny dostane přes fázi v šabloně směny (Směny → Šablony). Nepoužitá šablona se nikomu neukáže.</p>
                </div>
            );
        }
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                {nadpis('Kde se používá')}
                {(s.pouziti || []).length === 0 && <p className="text-xs" style={{ color: '#F59E0B', margin: 0 }}>V žádné šabloně směny. Přidej ji do fáze ve Směnách → Šablony.</p>}
                {(s.pouziti || []).map((x, i) => <div key={i}>{r(`${x.kod} ${x.nazev}`, x.faze)}</div>)}
                {nadpis('Posledních 30 dní')}
                {r('Vyplněno', `${s.stat?.vyplneno ?? 0}×`)}
                {r('Dokončeno', `${s.stat?.hotovo ?? 0}×`)}
                {r('Odchylky', s.stat?.odchylky ?? 0, true)}
                {r('Průměrně trvá', s.stat?.trvani_min != null ? `${s.stat.trvani_min} min` : '—')}
            </div>
        );
    };

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />

            {editPolozka && (
                <PolozkaEditor key={editPolozka.index ?? 'nova'} polozka={editPolozka.polozka} parametry={parametry} isDark={isDark} isMobile={isMobile}
                    iStyle={inputStyle} mutedColor={muted} topOffset={top} onClose={() => setEditPolozka(null)}
                    onSmazat={editPolozka.index != null && detail.polozky.length > 1 ? async () => { if (await uloz({ polozky: detail.polozky.filter((_, j) => j !== editPolozka.index) })) setEditPolozka(null); } : null}
                    onUlozit={async (p) => {
                        const polozky = editPolozka.index == null ? [...detail.polozky, p] : detail.polozky.map((x, j) => (j === editPolozka.index ? p : x));
                        if (await uloz({ polozky })) setEditPolozka(null);
                    }} />
            )}

            {editHlavicka && (
                <SidePanel open onClose={() => setEditHlavicka(null)} title="Upravit šablonu" isDark={isDark} isMobile={isMobile} width={420} modal={false} accent={SECTION_COLOR} topOffset={top}
                    footer={(
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', padding: '12px 16px' }}>
                            <button type="button" onClick={async () => {
                                if (!confirm('Archivovat šablonu? Z nových směn zmizí, historie zůstane.')) return;
                                await fetch(`/api/checklisty/sablony/${detail.id}`, { method: 'DELETE', headers: hlavicky() });
                                setEditHlavicka(null); setVybranyId(null); nacti();
                            }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', color: '#EF4444', background: 'rgba(239,68,68,0.08)', fontSize: 13 }}><Trash2 size={14} /> Archivovat</button>
                            <button type="button" disabled={!editHlavicka.nazev.trim()} onClick={async () => { if (await uloz({ nazev: editHlavicka.nazev.trim(), popis: editHlavicka.popis || null })) setEditHlavicka(null); }}
                                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', background: SECTION_COLOR, color: '#fff', fontWeight: 700, fontSize: 13 }}>Uložit</button>
                        </div>
                    )}>
                    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div><label style={{ fontSize: 11, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Název</label>
                            <input value={editHlavicka.nazev} onChange={e => setEditHlavicka(h => ({ ...h, nazev: e.target.value }))} style={inputStyle} /></div>
                        <div><label style={{ fontSize: 11, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Popis</label>
                            <textarea value={editHlavicka.popis} onChange={e => setEditHlavicka(h => ({ ...h, popis: e.target.value }))} rows={3} style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical' }} /></div>
                    </div>
                </SidePanel>
            )}

            {nova !== null && (
                <ModalShell onClose={() => setNova(null)} isDark={isDark} barva={SECTION_COLOR} ikona={ListChecks} nadpis="Nová šablona" sirka={420}
                    paticka={(
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => setNova(null)} style={tlac}>Zrušit</button>
                            <button type="button" onClick={zalozit} disabled={!nova.trim()} style={{ ...tlac, background: SECTION_COLOR, color: '#fff', border: 'none', opacity: nova.trim() ? 1 : 0.5 }}>Založit</button>
                        </div>
                    )}>
                    <div style={{ padding: 16 }}>
                        <input autoFocus value={nova} onChange={e => setNova(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') zalozit(); }} placeholder="např. Obsluha – Otevření lokálu" style={inputStyle} />
                        <p className="text-xs" style={{ color: muted, marginTop: 8 }}>Položky přidáš v detailu. Do směny ji pak zařadíš ve Směnách → Šablony.</p>
                    </div>
                </ModalShell>
            )}
        </div>
    );
}
